import React, { useMemo, useState, useEffect } from 'react';
import { Layout } from 'plotly.js';
import dayjs from 'dayjs';
import Typography from '@mui/material/Typography';
import Plot from './PlotlyCustom';
import { stackIcon, gridIcon, legendIcon, highLightIcon, ComparePlotData } from './plotCommon';
import {
  getShowStackedTraces,
  getShowPlotGrid,
  getShowPlotLegend,
  getSelectedEndDate,
  getSelectedStartDate,
  getHighlightedItem,
  getShowPlotHighlight,
} from '../../state/selectors';
import {
  setSelectedEndDate,
  setSelectedStartDate,
  setStackedTraces,
  setPlotGrid,
  setPlotLegend,
  setPlotHighlight,
} from '../../state/actions';
import { VarName } from '../../utils/varNames';
import { getEndOfDay, getStartOfDay } from '../../utils/functions';
import useStyles from '../../styles';
import { useAppDispatch, useAppSelector } from '../../state/store';

interface StackedPlotProps {
  plotData: ComparePlotData[];
  layout: Partial<Layout>;
  varNames: VarName[];
}

export function StackedPlot({ plotData, layout, varNames }: StackedPlotProps): JSX.Element {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const showStackedTraces = useAppSelector(getShowStackedTraces);
  const showPlotGrid = useAppSelector(getShowPlotGrid);
  const showPlotLegend = useAppSelector(getShowPlotLegend);
  const showPlotHightLight = useAppSelector(getShowPlotHighlight);
  const startDate = useAppSelector(getSelectedStartDate);
  const endDate = useAppSelector(getSelectedEndDate);
  const highLightedItem = useAppSelector(getHighlightedItem);
  const [hoveredId, setHoveredId] = useState('');

  useEffect(() => {
    if (showPlotHightLight) setHoveredId(highLightedItem.id);
  }, [showPlotHightLight, highLightedItem.id]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const hasNoPlotData = plotData.filter((data: ComparePlotData) => data.x.length > 0).length === 0;

  function handleRelayout(event: Plotly.PlotRelayoutEvent) {
    if (event['xaxis.range[0]'] && event['xaxis.range[1]']) {
      let newStart = new Date(event['xaxis.range[0]']);
      let newEnd = new Date(event['xaxis.range[1]']);
      // Don't dispatch if it is a zoom into current data, only if the new range is larger (pan)
      if (dayjs(newStart).isBefore(dayjs(startDate)) || dayjs(newEnd).isAfter(dayjs(endDate))) {
        // If dragged to the future limit to now and keep range from drag
        const now = new Date();
        if (newEnd > now) {
          const diff = Math.max(newEnd.getTime() - now.getTime(), 24 * 3600 * 1000);
          newEnd = getEndOfDay(now);
          newStart = getStartOfDay(new Date(newStart.getTime() - diff));
        }
        dispatch(setSelectedStartDate(newStart));
        dispatch(setSelectedEndDate(newEnd));
      }
    }
  }

  const handleHover = (event: Plotly.PlotMouseEvent) => {
    const { points } = event;
    const hoveredData = points[0].data as ComparePlotData;
    const sensorId = hoveredData?.hoverId;
    if (showPlotHightLight && sensorId) setHoveredId(sensorId);
  };

  // highlight hovered trace with opacity
  const updatedPlotData = useMemo(() => {
    let data = plotData;

    if (hoveredId) {
      data = plotData.map((item) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const itemId = item?.hoverId ?? '';
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const fillColor = item?.fillcolor;
        const isHovered = itemId === hoveredId;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const lineProps = item?.line;

        return {
          ...item,
          opacity: isHovered ? 1 : 0.3,
          // update fill color transparency for filled scatter plots (mainly in wifi, energy and BLE graphs)
          ...(fillColor && {
            fillcolor: fillColor.replace(/,[\s]*[\d.]+\)$/, `,${isHovered ? 0.5 : 0.1})`),
          }),
          // update line color transparency
          line: {
            ...lineProps,
            ...(lineProps && {
              color: fillColor?.replace(/,[\s]*[\d.]+\)$/, `,${isHovered ? 1 : 0.3})`),
            }),
          },
        };
      });
    }
    return data;
  }, [hoveredId, plotData]);

  return (
    <div style={{ borderRadius: '8px' }}>
      <Plot
        data={
          hasNoPlotData
            ? [{ x: [startDate, endDate], y: [0, 0], marker: { opacity: 0 }, mode: 'markers' }] // Fake data to keep xaxis range
            : updatedPlotData
        }
        layout={layout}
        useResizeHandler
        onRelayout={(e) => handleRelayout(e)}
        style={{
          width: '100%',
          height: varNames.length > 1 ? `${200 * varNames.length}px` : '400px',
        }}
        onHover={handleHover}
        onUnhover={() => setHoveredId('')}
        config={{
          modeBarButtonsToAdd: [
            {
              name: `allowHover-${showPlotHightLight}`,
              title: `${showPlotHightLight ? 'Disable' : 'Enable'} Highlight`,
              icon: highLightIcon,
              click: () => dispatch(setPlotHighlight(!showPlotHightLight)),
            },
            {
              name: `stackTraces-${showStackedTraces}`,
              title: 'Stacked/Alternate Traces',
              icon: stackIcon,
              click: () => dispatch(setStackedTraces(!showStackedTraces)),
            },
            {
              name: `showGrid-${showPlotGrid}`,
              title: `${showPlotGrid ? 'Hide' : 'Show'} Gridlines`,
              icon: gridIcon,
              click: () => dispatch(setPlotGrid(!showPlotGrid)),
            },
            {
              name: `showLegend-${showPlotLegend}`,
              title: `${showPlotLegend ? 'Hide' : 'Show'} Legend`,
              icon: legendIcon,
              click: () => dispatch(setPlotLegend(!showPlotLegend)),
            },
          ],
          modeBarButtonsToRemove: [
            'lasso2d',
            'select2d',
            'zoomOut2d',
            // 'zoom2d',
            // 'pan2d',
            'zoomIn2d',
            'autoScale2d',
          ],
          displaylogo: false,
          doubleClick: 'autosize',
        }}
      />
      {hasNoPlotData && (
        <Typography variant="body1" className={classes.plotAlertText}>
          No data (Select different sensors, data type or time period)
        </Typography>
      )}
    </div>
  );
}
export default StackedPlot;
